import * as React from 'react';

import { cn } from '@/lib/utils';
import {
  clampWidth,
  nextWidthFromDrag,
  readStoredWidth,
  viewportDrawerBounds,
  writeStoredWidth,
  DRAWER_KEYBOARD_STEP,
  MAX_VIEWPORT_DRAWER_FRACTION,
  MIN_VIEWPORT_DRAWER_WIDTH,
  type DrawerWidthBounds,
} from '@/lib/resizableSheet';

export interface UseFloatingDrawerResizeOptions {
  widthPercent: number;
  defaultWidthPx?: number;
  minWidthPx?: number;
  maxWidthFraction?: number;
  storageKey?: string;
  enabled?: boolean;
}

export interface UseFloatingDrawerResizeResult {
  width: number;
  bounds: DrawerWidthBounds;
  drawerProps: { width: number; resizeHandle: React.ReactNode };
  setWidth: (next: number) => void;
}

const COMPACT_BELOW_PX = 1024;

function readViewportWidth(): number {
  return typeof window === 'undefined' ? 1440 : window.innerWidth;
}

export function useFloatingDrawerResize({
  widthPercent,
  defaultWidthPx,
  minWidthPx = MIN_VIEWPORT_DRAWER_WIDTH,
  maxWidthFraction = MAX_VIEWPORT_DRAWER_FRACTION,
  storageKey,
  enabled = true,
}: UseFloatingDrawerResizeOptions): UseFloatingDrawerResizeResult {
  const readBounds = React.useCallback(
    () =>
      viewportDrawerBounds({
        viewportWidth: readViewportWidth(),
        widthPercent,
        defaultWidthPx,
        minWidthPx,
        maxWidthFraction,
      }),
    [widthPercent, defaultWidthPx, minWidthPx, maxWidthFraction],
  );

  const [bounds, setBounds] = React.useState<DrawerWidthBounds>(readBounds);
  const [compact, setCompact] = React.useState(() => readViewportWidth() < COMPACT_BELOW_PX);
  const [width, setWidth] = React.useState<number>(() => {
    const b = readBounds();
    return readStoredWidth(storageKey, b.defaultWidth, b.minWidth, b.maxWidth);
  });

  const dragRef = React.useRef<{ startX: number; startW: number } | null>(null);
  const boundsRef = React.useRef(bounds);
  boundsRef.current = bounds;

  const endDrag = React.useCallback(() => {
    if (!dragRef.current) return;
    dragRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  React.useEffect(() => {
    if (!enabled) return;
    const onResize = () => {
      setCompact(readViewportWidth() < COMPACT_BELOW_PX);
      const b = readBounds();
      setBounds(b);
      setWidth((w) => clampWidth(w, b.minWidth, b.maxWidth));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [enabled, readBounds]);

  React.useEffect(() => {
    if (!enabled) return;
    const onMove = (e: PointerEvent) => {
      const anchor = dragRef.current;
      if (!anchor) return;
      const b = boundsRef.current;
      setWidth(
        nextWidthFromDrag(
          'right',
          anchor.startX,
          anchor.startW,
          e.clientX,
          b.minWidth,
          b.maxWidth,
        ),
      );
    };
    const onUp = () => {
      if (!dragRef.current) return;
      setWidth((w) => {
        writeStoredWidth(storageKey, w);
        return w;
      });
      endDrag();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [enabled, storageKey, endDrag]);

  React.useEffect(() => endDrag, [endDrag]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startW: width };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const commit = (next: number) => {
    const b = boundsRef.current;
    const clamped = clampWidth(next, b.minWidth, b.maxWidth);
    setWidth(clamped);
    writeStoredWidth(storageKey, clamped);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    let next: number | null = null;
    if (e.key === 'ArrowLeft') next = width + DRAWER_KEYBOARD_STEP;
    else if (e.key === 'ArrowRight') next = width - DRAWER_KEYBOARD_STEP;
    else if (e.key === 'Home') next = boundsRef.current.minWidth;
    else if (e.key === 'End') next = boundsRef.current.maxWidth;
    if (next === null) return;
    e.preventDefault();
    commit(next);
  };

  const resizeHandle = enabled ? (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize drawer"
      aria-valuenow={width}
      aria-valuemin={bounds.minWidth}
      aria-valuemax={bounds.maxWidth}
      tabIndex={0}
      data-testid="floating-drawer-resize-handle"
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onDoubleClick={() => commit(bounds.defaultWidth)}
      className={cn(
        'group absolute inset-y-0 -left-4 z-30 hidden w-8 cursor-col-resize touch-none select-none items-center justify-center lg:flex',
        'focus-visible:outline-none',
      )}
    >
      <span className="flex h-11 w-6 items-center justify-center rounded-lg border border-[#E4E7EC] bg-white text-[#707684] shadow-[0_4px_10px_rgba(16,24,40,0.14)] transition group-hover:border-[#D0D5DD] group-hover:text-[#101828] group-focus-visible:border-brand group-focus-visible:text-[#101828]">
        <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
          <circle cx="3" cy="4" r="1.1" /><circle cx="7" cy="4" r="1.1" />
          <circle cx="3" cy="8" r="1.1" /><circle cx="7" cy="8" r="1.1" />
          <circle cx="3" cy="12" r="1.1" /><circle cx="7" cy="12" r="1.1" />
        </svg>
      </span>
    </div>
  ) : null;

  const effectiveWidth = compact ? readViewportWidth() : width;

  return {
    width: effectiveWidth,
    bounds,
    drawerProps: { width: effectiveWidth, resizeHandle: compact ? null : resizeHandle },
    setWidth: commit,
  };
}
